import { useEffect, useRef, useState } from 'react';
import { ApiFailure, api, type ReviewEntry, type ReviewSide } from '../lib/api';

/**
 * Merge interface — Appendix C.2 Phase 2 deliverable 11.
 *
 * The one place in the application where two contacts become one. It is not
 * reachable from the loader, which merges nothing, and it never guesses:
 *
 *  - the surviving record is chosen explicitly, never inferred
 *  - a reason is required; §3.6 wants a named person and a logged decision
 *  - every field that differs is shown before anything is written
 *  - a cross-entity pair is refused by the server, and the refusal is shown
 *    here as what it is rather than as a generic failure
 *  - the merge is reversible from the merge history
 */

const FIELDS: { key: keyof ReviewSide; label: string; numeric?: boolean }[] = [
  { key: 'name', label: 'Name' },
  { key: 'company', label: 'Company' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone', numeric: true },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'tier', label: 'Tier' },
  { key: 'category', label: 'Category' },
  { key: 'legacy', label: 'Legacy ID', numeric: true },
];

const REFUSAL_TEXT: Record<string, string> = {
  pillar_conflict:
    'These two records route to different legal entities. CLM Construction LLC and CALM Construction ' +
    'LLC are never blended into one record, so confirm which entity owns this contact before merging.',
  already_merged: 'That record is already merged into another contact.',
  winner_is_merged: 'The record you chose to keep is itself merged into another contact.',
  review_entry_not_open: 'This review entry has already been resolved by someone else. Reload the queue.',
  same_contact: 'A contact cannot be merged into itself.',
  reason_required: 'A merge is recorded with a reason or not at all.',
};

export function MergeInterface({
  entry,
  onClose,
  onMerged,
}: {
  entry: ReviewEntry;
  onClose: () => void;
  onMerged: () => void;
}): JSX.Element {
  const [winner, setWinner] = useState<'a' | 'b' | null>(null);
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dialogRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sides = { a: entry.a, b: entry.b };
  const differing = FIELDS.filter((field) => (entry.a[field.key] ?? null) !== (entry.b[field.key] ?? null));
  const entityConflict =
    (entry.a.pillars?.length ?? 0) > 0 &&
    (entry.b.pillars?.length ?? 0) > 0 &&
    !entry.a.pillars!.some((p) => entry.b.pillars!.includes(p));

  async function submit(): Promise<void> {
    if (!winner) {
      setRefusal('Choose which record survives. The interface does not pick one for you.');
      return;
    }
    if (reason.trim().length < 3) {
      setRefusal(REFUSAL_TEXT.reason_required!);
      return;
    }
    const winnerId = sides[winner].id;
    const loserId = sides[winner === 'a' ? 'b' : 'a'].id;
    if (!winnerId || !loserId) {
      setRefusal('One side of this pair has no contact record, so there is nothing to merge.');
      return;
    }
    setBusy(true);
    try {
      await api.mergeContacts({ winnerId, loserId, reason: reason.trim(), reviewQueueId: entry.id });
      onMerged();
    } catch (failure) {
      if (failure instanceof ApiFailure) {
        setRefusal(REFUSAL_TEXT[failure.body.error] ?? `Refused: ${failure.body.error}`);
      } else {
        setRefusal('The merge could not be completed.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-navy/60 p-4"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="merge-heading"
        tabIndex={-1}
        className="max-h-full w-full max-w-4xl overflow-y-auto rounded-card border border-border bg-white p-5"
      >
        <header className="flex items-start justify-between gap-3">
          <div>
            <h2 id="merge-heading" className="text-h1 font-semibold text-text-primary">
              Merge two contacts
            </h2>
            <p className="numeric text-numeric text-text-secondary">
              {entry.decision_key}
              {entry.legacy_rows ? ` · legacy rows ${entry.legacy_rows}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary rounded-card px-3 text-body font-medium"
          >
            Cancel
          </button>
        </header>

        {entityConflict ? (
          <p className="mt-3 rounded-card border border-warning px-3 py-2 text-body text-warning">
            {REFUSAL_TEXT.pillar_conflict}
          </p>
        ) : null}

        <fieldset className="mt-4">
          <legend className="micro-label text-text-secondary">Which record survives</legend>
          <div className="mt-2 grid gap-3 lg:grid-cols-2">
            {(['a', 'b'] as const).map((key) => {
              const side = sides[key];
              const selected = winner === key;
              return (
                <label
                  key={key}
                  className={`block cursor-pointer rounded-card border p-3 ${
                    selected ? 'border-royal bg-surface' : 'border-border bg-white'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="merge-winner"
                      value={key}
                      checked={selected}
                      onChange={() => setWinner(key)}
                      className="h-touch w-touch"
                    />
                    <span className="text-h3 font-semibold text-text-primary">{side.name ?? '—'}</span>
                  </span>
                  <span className="micro-label mt-1 block text-text-secondary">
                    Keep this record{side.pillars?.length ? ` · ${side.pillars.join(', ')}` : ''}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className="mt-4 overflow-hidden rounded-card border border-border">
          <table className="w-full border-collapse text-body">
            <caption className="sr-only">Field comparison for the two records</caption>
            <thead>
              <tr className="table-header bg-navy text-white">
                <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">Field</th>
                <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">Record A</th>
                <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">Record B</th>
              </tr>
            </thead>
            <tbody>
              {FIELDS.map((field) => {
                const a = (entry.a[field.key] as string | null) ?? null;
                const b = (entry.b[field.key] as string | null) ?? null;
                const differs = a !== b;
                return (
                  <tr key={String(field.key)} className="table-row border-t border-border">
                    <th scope="row" className="px-3 py-2 text-left font-medium text-text-secondary">
                      {field.label}
                      {differs ? <span className="micro-label ml-2 text-warning">differs</span> : null}
                    </th>
                    <td className={`px-3 py-2 ${field.numeric ? 'numeric' : ''}`}>{a ?? '—'}</td>
                    <td className={`px-3 py-2 ${field.numeric ? 'numeric' : ''}`}>{b ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-2 text-body text-text-secondary">
          {differing.length} field{differing.length === 1 ? '' : 's'} differ. The surviving record keeps its
          own values; empty fields are filled from the other record, and nothing is overwritten. The losing
          record is retained and hidden, never deleted, so this merge can be undone.
        </p>

        <label htmlFor="merge-reason" className="micro-label mt-4 block text-text-secondary">
          Reason (recorded against your name in the audit log)
        </label>
        <textarea
          id="merge-reason"
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className="mt-1 w-full rounded-card border border-border bg-white px-3 py-2 text-body text-text-primary"
          placeholder="e.g. CALM confirmed on 6 Sept that these two records are one contact"
        />

        {refusal ? (
          <p role="alert" className="mt-3 rounded-card border border-danger px-3 py-2 text-body text-danger">
            {refusal}
          </p>
        ) : null}

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary rounded-card px-4 text-body font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !winner || reason.trim().length < 3}
            onClick={() => void submit()}
            className="btn-primary rounded-card px-4 text-body font-medium disabled:opacity-40"
          >
            {busy ? 'Merging…' : 'Merge and record the decision'}
          </button>
        </div>
      </div>
    </div>
  );
}
